import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import { CheckIcon } from '@segment/matcha/components/Icons/Common'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import UseAIImage from './UseAiImage'

const copy = {
  title: `Use AI to boost selling opportunities`,
  subtitle: `Identify which customers are primed for purchase and which products they’re most inclined to buy without needing to tap technical teams.`,
  elements: [
    {
      title: `Take the guesswork out of customer recommendations`,
      description: `With the combined power of CustomerAI Predictions and CustomerAI Recommendations (Beta), you can easily identify and launch personalized ads across platforms, targeted to audiences based on their likelihood to purchase specific products, brands, categories, and more.`,
    },
    {
      title: `Unlock advanced analytics for more upsell and cross-sell opportunities`,
      description: `Access all of Segment’s real-time profile data in your warehouse to build machine-learning models that unlock even more upsell and cross-sell opportunities to drive repeat purchase and increase lifetime value.`,
    },
    {
      title: `Boost cross-sell and upsell with Segment`,
      description: `Segment combines the power of unified customer data with powerful audience segmentation, journey orchestration, and AI to help marketers deliver relevant, trusted, and timely experiences that grow customer lifetime value at every step of the customer journey and across any channel.`,
    },
  ],
}

const UseAi = () => {
  const theme = useTheme()
  return (
    <Wrapper>
      <StyledSectionBaseTitle variant="noCopy">
        <Section.Contents>
          <CopyTextBase centered={true}>
            <CopyText.Heading tag="h2">{copy.title}</CopyText.Heading>
            <CopyText.Description>{copy.subtitle}</CopyText.Description>
          </CopyTextBase>
        </Section.Contents>
      </StyledSectionBaseTitle>
      <StyledSectionBaseContent reverse={true} variant="2Col">
        <Section.Contents>
          <UseAIImage />
        </Section.Contents>
        <Section.Copy alignSelf="center">
          <ColumnTrack>
            {copy.elements.map(({ title, description }, key) => (
              <StyledFeatureBoxBase key={key} alignment="left" appearance="twilioLight" headerPosition="left">
                <FeatureBox.Header>
                  <StyledCheckIcon size="small" />
                </FeatureBox.Header>
                <FeatureBox.Body>
                  <FeatureBox.Title>{title}</FeatureBox.Title>
                  <FeatureBox.Text
                    tokens={{
                      color: theme.palette.twilio.gray[80],
                    }}
                    variant="pMedium"
                  >
                    {description}
                  </FeatureBox.Text>
                </FeatureBox.Body>
              </StyledFeatureBoxBase>
            ))}
          </ColumnTrack>
        </Section.Copy>
      </StyledSectionBaseContent>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const StyledCheckIcon = styled(CheckIcon)`
  margin-top: 5px;
`

const StyledFeatureBoxBase = styled(FeatureBoxBase)`
  gap: 0;
  grid-template-columns: 22px auto;
`

const StyledSectionBaseTitle = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 80px;
    padding-bottom: 0;
  }
`

const StyledSectionBaseContent = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 80px;
    padding-bottom: 80px;
  }
`

export default UseAi
